/**
 * Single source of truth for all Onboarding Tracking reader-facing copy (v2).
 * UI components must import labels from here — no hardcoded jargon in .vue files.
 */

/** Ordered journey steps shown on the ribbon (OPERATIONS excluded). */
export const JOURNEY_STEP_ORDER = [
  'ENTRY',
  'DETAILS',
  'ASSESSMENT',
  'ACCOUNT',
  'PAYMENT',
  'BOOKING',
  'CONFIRMATION',
  'PROFILE',
  'ADDITIONAL_DETAILS'
];

/** Patient-facing registration stepper (matches widget → /register/payment UI). */
export const PATIENT_UI_STEP_ORDER_GUEST = [
  'WIDGET',
  'CHECKOUT',
  'CONFIRMATION',
  'PROFILE',
  'ADDITIONAL_DETAILS'
];

export const PATIENT_UI_STEP_ORDER_EXISTING = [
  'WIDGET',
  'CHECKOUT',
  'CONFIRMATION',
  'ADDITIONAL_DETAILS'
];

/** Without widget — direct payment / register entry. */
export const PATIENT_UI_STEP_ORDER_GUEST_NO_WIDGET = [
  'CHECKOUT',
  'CONFIRMATION',
  'PROFILE',
  'ADDITIONAL_DETAILS'
];

export const PATIENT_UI_STEP_ORDER_EXISTING_NO_WIDGET = [
  'CHECKOUT',
  'CONFIRMATION',
  'ADDITIONAL_DETAILS'
];

/** Internal checkout micro-steps that map to the Checkout UI stage. */
export const CHECKOUT_MICRO_STEPS = [
  'ENTRY',
  'DETAILS',
  'ASSESSMENT',
  'ACCOUNT',
  'PAYMENT',
  'BOOKING'
];

export const WIDGET_STEPS = ['WIDGET'];

export const STEPS = {
  WIDGET: {
    code: 'WIDGET',
    label: 'Widget',
    short: 'Widget',
    icon: 'calendar',
    patientStage: 'WIDGET'
  },
  ENTRY: {
    code: 'ENTRY',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'door-open',
    patientStage: 'CHECKOUT',
    insideCheckout: 'Started'
  },
  DETAILS: {
    code: 'DETAILS',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'pencil',
    patientStage: 'CHECKOUT',
    insideCheckout: 'Patient details'
  },
  ASSESSMENT: {
    code: 'ASSESSMENT',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'clipboard',
    patientStage: 'CHECKOUT',
    insideCheckout: 'Health questions'
  },
  ACCOUNT: {
    code: 'ACCOUNT',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'user',
    patientStage: 'CHECKOUT',
    insideCheckout: 'Account'
  },
  PAYMENT: {
    code: 'PAYMENT',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'credit-card',
    patientStage: 'CHECKOUT',
    insideCheckout: 'Payment'
  },
  BOOKING: {
    code: 'BOOKING',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'calendar',
    patientStage: 'CHECKOUT',
    insideCheckout: 'Appointment slot'
  },
  CONFIRMATION: {
    code: 'CONFIRMATION',
    label: 'Confirmation',
    short: 'Confirmation',
    icon: 'check',
    patientStage: 'CONFIRMATION'
  },
  PROFILE: {
    code: 'PROFILE',
    label: 'Complete your profile',
    short: 'Complete your profile',
    icon: 'id-card',
    patientStage: 'PROFILE'
  },
  ADDITIONAL_DETAILS: {
    code: 'ADDITIONAL_DETAILS',
    label: 'Additional details',
    short: 'Additional details',
    icon: 'file-text',
    patientStage: 'ADDITIONAL_DETAILS'
  },
  CHECKOUT: {
    code: 'CHECKOUT',
    label: 'Checkout',
    short: 'Checkout',
    icon: 'shopping-cart',
    patientStage: 'CHECKOUT'
  },
  SUPPORT: {
    code: 'SUPPORT',
    label: 'Talked to support',
    short: 'Support',
    icon: 'phone',
    hidden: true
  },
  OPERATIONS: {
    code: 'OPERATIONS',
    label: 'System change',
    short: 'Operations',
    icon: 'cog',
    hidden: true
  }
};

export const STATUSES = {
  ACTIVE: {
    code: 'ACTIVE',
    label: 'On the site now',
    state: 'good',
    icon: 'circle'
  },
  IDLE: {
    code: 'IDLE',
    label: 'Paused',
    labelWithMinutes: 'Paused — no activity for {n} minutes',
    state: 'watch',
    icon: 'pause'
  },
  ABANDONED: {
    code: 'ABANDONED',
    label: 'Left without booking',
    state: 'act',
    icon: 'sign-out'
  },
  COMPLETED: {
    code: 'COMPLETED',
    label: 'Finished all steps',
    state: 'good',
    icon: 'check'
  }
};

/** Reader vocabulary — concept renames for UI chrome. */
export const VOCAB = {
  journey: 'Journey',
  action: 'Action',
  wherePeopleStop: 'Where people stop',
  finishedBooking: 'Finished booking',
  leftWithoutBooking: 'Left without booking',
  appointmentSlots: 'Appointment slots',
  peopleWhoWanted: 'People who wanted that time',
  howFull: 'How full it was',
  problem: 'Problem',
  fix: 'Fix',
  cameBack: 'Came back',
  cameFrom: 'Came from',
  timeOnStep: 'Time on this step',
  paused: 'Paused'
};

/** Past-tense action sentences for timeline stories. */
const ACTION_TEMPLATES = {
  SESSION_STARTED: 'Arrived on the site',
  STEP_ENTERED: 'Moved to a new step',
  PAGE_VIEWED: 'Opened {step}',
  STEP_COMPLETED: 'Finished a step',
  STEP_FAILED: 'Something went wrong on {step}',
  DETAILS_COMPLETED: 'Submitted their details',
  DETAILS_FAILED: 'Something went wrong on {step}',
  ASSESSMENT_COMPLETED: 'Finished the health questions',
  ASSESSMENT_FAILED: 'Something went wrong on {step}',
  OTP_REQUESTED: 'We sent them a verification code',
  OTP_SENT: 'We sent them a verification code',
  OTP_VERIFIED: 'Verified their phone or email',
  OTP_FAILED: 'Typed the wrong verification code',
  REGISTERED: 'Created their account',
  REGISTRATION_FAILED: 'Something went wrong on {step}',
  PAYMENT_STARTED: 'Started paying',
  PAYMENT_SUCCEEDED: 'Payment went through',
  PAYMENT_FAILED: 'Payment didn\'t go through — {plainReason}',
  PAYMENT_METHOD_SELECTED: 'Chose to pay by {method}',
  BOOKING_SUBMITTED: 'Submitted their booking',
  BOOKING_COMPLETED: 'Booked their appointment',
  BOOKING_FAILED: 'Something went wrong on {step}',
  PROFILE_COMPLETED: 'Set their password / finished profile',
  ADDITIONAL_DETAILS_COMPLETED: 'Submitted additional consultation details',
  JOURNEY_COMPLETED: 'Finished the full registration journey 🎉',
  FIELD_CLICKED: 'Tapped the {fieldLabel} box',
  CTA_CLICKED: 'Pressed {actionLabel}',
  SLOT_LIST_VIEWED: 'Looked at the available appointment times',
  SLOT_SELECTED: 'Picked {time}',
  SLOT_UNAVAILABLE: 'Could not find a suitable time',
  EXIT_REASON_SELECTED: 'Told us why they were leaving: {reason}',
  API_CALL: 'Called {apiPath} ({httpMethod}) — {durationMs}ms'
};

/** Problem cause + consequence for the reader. */
const PROBLEM_PATTERNS = [
  {
    test: /card.?declin|declined|CARD_DECLINED/i,
    headline: 'Their card was declined',
    detail: 'The bank said no, nothing wrong on our side'
  },
  {
    test: /3ds|3.?d.?s|secure.?check/i,
    headline: 'Card security check failed',
    detail: 'The bank asked for extra confirmation and it didn\'t complete'
  },
  {
    test: /otp.?expir|expired.?code/i,
    headline: 'Verification code ran out of time',
    detail: 'They took too long before typing it'
  },
  {
    test: /otp|verification|OTP_INVALID|OTP_FAILED/i,
    headline: 'Verification code did not work',
    detail: 'They may have typed the wrong code'
  },
  {
    test: /slot.?taken|slot.?unavail|NO_SLOTS/i,
    headline: 'The appointment was taken while they were booking',
    detail: 'Someone else got it first'
  },
  {
    test: /timeout|5\d\d|503|504|timed.?out/i,
    headline: 'Our system didn\'t respond in time',
    detail: 'This one is on us'
  },
  {
    test: /valid|VALIDATION|incomplete/i,
    headline: 'They typed something the form wouldn\'t accept',
    detail: 'Check the field hints on that step'
  },
  {
    test: /payment|PAYMENT/i,
    headline: 'Payment didn\'t go through',
    detail: 'Check whether it was the bank or our checkout'
  }
];

const UNKNOWN_ACTION = 'Something happened on this step';
const UNKNOWN_PROBLEM = {
  headline: 'An unexpected problem',
  detail: 'The technical team can see the details'
};

const REFERRER_LABELS = {
  google: 'Google Ads',
  facebook: 'Meta / Facebook',
  bing: 'Bing Ads',
  instagram: 'Meta / Facebook',
  email: 'Link in email',
  chatgpt: 'ChatGPT',
  website: 'Booking website',
  direct: 'Direct'
};

export const SOURCE_FILTER_OPTIONS = [
  { value: 'direct', label: 'Direct (typed URL)', hint: 'No referring website — bookmark, typed address, or in-app.' },
  { value: 'website', label: 'Booking website', hint: 'Clicked through from book.thegpservice.co.uk.' },
  { value: 'chatgpt', label: 'ChatGPT', hint: 'Arrived from ChatGPT.' },
  { value: 'google', label: 'Google Ads', hint: 'Google Ads click (gclid / utm_source=google).' },
  { value: 'facebook', label: 'Meta / Facebook', hint: 'Facebook or Instagram.' },
  { value: 'bing', label: 'Bing Ads', hint: 'Microsoft / Bing ads.' },
  { value: 'other', label: 'Other', hint: 'Any other campaign or referrer.' }
];

const DEVICE_LABELS = {
  mobile: 'On a phone',
  desktop: 'On a computer',
  tablet: 'On a tablet'
};

export function journeyNumberFromId(sessionId) {
  if (!sessionId) return 0;
  const raw = String(sessionId);
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash % 9000) + 1000;
}

export function formatJourneyId(sessionId) {
  const num = journeyNumberFromId(sessionId);
  return num ? `Journey #${num}` : 'Journey';
}

export function prettyStep(step, { short = false } = {}) {
  if (!step) return 'Unknown step';
  const code = String(step).toUpperCase();
  if (code === 'WIDGET') {
    return 'Widget';
  }
  // Map internal checkout micro-steps to the patient-facing "Checkout" label.
  if (CHECKOUT_MICRO_STEPS.includes(code)) {
    return 'Checkout';
  }
  const entry = STEPS[code];
  if (!entry || entry.hidden) {
    return 'Unknown step';
  }
  return short ? entry.short : entry.label;
}

/** Sub-label inside Checkout only (admin drill-down chips). */
export function prettyInsideCheckout(step) {
  if (!step) return '';
  const entry = STEPS[String(step).toUpperCase()];
  return (entry && entry.insideCheckout) || '';
}

export function stepIcon(step) {
  const entry = STEPS[step];
  return (entry && entry.icon) || 'circle';
}

export function prettyStatus(status, { idleMinutes } = {}) {
  if (!status) return 'Unknown';
  const entry = STATUSES[status];
  if (!entry) return 'Unknown';
  if (status === 'IDLE' && idleMinutes != null && entry.labelWithMinutes) {
    return entry.labelWithMinutes.replace('{n}', String(idleMinutes));
  }
  return entry.label;
}

export function statusState(status) {
  const entry = STATUSES[status];
  return (entry && entry.state) || 'watch';
}

export function statusIcon(status) {
  const entry = STATUSES[status];
  return (entry && entry.icon) || 'circle';
}

function interpolate(template, vars = {}) {
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = vars[key];
    return value != null && value !== '' ? String(value) : '';
  });
}

function metadataValue(event, keys) {
  const meta = (event && event.metadata) || {};
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (meta[key] != null && meta[key] !== '') {
      return meta[key];
    }
  }
  return null;
}

function prettyCtaAction(action) {
  const key = String(action || '').trim();
  const map = {
    continue_to_payment: 'Book Consultation',
    book_consultation: 'Book Consultation',
    book_consultation_goodbody: 'Book Consultation'
  };
  if (map[key]) return map[key];
  if (!key) return '';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function prettyAction(eventName, event = {}) {
  if (!eventName) return UNKNOWN_ACTION;
  const template = ACTION_TEMPLATES[eventName];
  if (!template) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[trackingWords] Unmapped action:', eventName);
    }
    return UNKNOWN_ACTION;
  }

  const step = prettyStep(event.step || metadataValue(event, ['step']));
  const plainReason = prettyProblem(event.errorCode || metadataValue(event, ['errorCode', 'reason']), { headlineOnly: true });
  let fieldLabel = metadataValue(event, ['fieldLabel', 'field', 'fieldName']) || 'a form';
  // Soften noisy / placeholder labels for sentence templates
  const noisy = /^(input|select|button)$/i.test(String(fieldLabel).trim())
    || String(fieldLabel).length > 55
    || /consultationcall|great for discussions/i.test(String(fieldLabel));
  if (noisy) {
    const name = String(metadataValue(event, ['fieldName']) || '').toLowerCase();
    if (name.includes('condition')) fieldLabel = 'appointment reason';
    else if (name.includes('line1')) fieldLabel = 'address line 1';
    else if (name.includes('line2')) fieldLabel = 'address line 2';
    else if (name === 'city') fieldLabel = 'town / city';
    else if (name === 'county') fieldLabel = 'county';
    else if (name.includes('postcode')) fieldLabel = 'postcode';
    else if (name.includes('discount') || name === 'input') fieldLabel = 'payment / discount';
    else if (/q4a|video|audio|consultation/.test(name)) fieldLabel = 'consultation type';
    else fieldLabel = 'form';
  } else {
    const ph = String(fieldLabel).toLowerCase();
    if (ph === 'select a reason') fieldLabel = 'appointment reason';
    else if (ph.startsWith('enter ')) fieldLabel = ph.replace(/^enter\s+/, '');
    else if (ph.startsWith('select ')) fieldLabel = ph.replace(/^select\s+/, '');
  }
  const actionLabel = prettyCtaAction(
    metadataValue(event, ['actionLabel', 'label', 'buttonLabel', 'action'])
  ) || 'a button';
  const time = metadataValue(event, ['time', 'slotTime', 'selectedTime']) || 'a time';
  const method = metadataValue(event, ['method', 'paymentMethod']) || 'card';
  const reason = metadataValue(event, ['reason', 'exitReason']) || 'not said';
  const httpMethod = metadataValue(event, ['httpMethod']) || 'GET';
  const apiPath = event.apiPath || metadataValue(event, ['apiPath']) || 'an API';
  const durationMs = event.durationMs != null ? event.durationMs : metadataValue(event, ['durationMs']);

  let sentence = interpolate(template, {
    step,
    plainReason,
    fieldLabel,
    actionLabel,
    time,
    method,
    reason,
    httpMethod,
    apiPath,
    durationMs: durationMs != null ? durationMs : '?'
  });

  if (eventName === 'FIELD_CLICKED' && sentence.includes('a form')) {
    sentence = 'Tapped a box on the form';
  }
  if (eventName === 'CTA_CLICKED' && sentence.includes('a button')) {
    sentence = 'Pressed a button';
  }

  return sentence.replace(/\s+/g, ' ').trim();
}

export function prettyProblem(code, { headlineOnly = false } = {}) {
  if (!code) {
    return headlineOnly
      ? UNKNOWN_PROBLEM.headline
      : `${UNKNOWN_PROBLEM.headline} — ${UNKNOWN_PROBLEM.detail}`;
  }

  const raw = String(code);
  for (let i = 0; i < PROBLEM_PATTERNS.length; i += 1) {
    const pattern = PROBLEM_PATTERNS[i];
    if (pattern.test.test(raw)) {
      return headlineOnly
        ? pattern.headline
        : `${pattern.headline} — ${pattern.detail}`;
    }
  }

  return headlineOnly
    ? UNKNOWN_PROBLEM.headline
    : `${UNKNOWN_PROBLEM.headline} — ${UNKNOWN_PROBLEM.detail}`;
}

export function formatReferrer({ utmSource, utmMedium, referrer, gclid, msclkid, sourceLabel } = {}) {
  if (sourceLabel) return sourceLabel;
  const source = String(utmSource || '').toLowerCase();
  const medium = String(utmMedium || '').toLowerCase();
  const ref = String(referrer || '').toLowerCase();

  if (medium === 'email') return REFERRER_LABELS.email;
  if (source.includes('chatgpt') || ref.includes('chatgpt') || ref.includes('openai.com')) return REFERRER_LABELS.chatgpt;
  if (gclid || source === 'google' || source.includes('googleads') || ref.includes('googleads')) return REFERRER_LABELS.google;
  if (msclkid || source.includes('bing') || ref.includes('bing.com')) return REFERRER_LABELS.bing;
  if (source.includes('facebook') || source === 'fb' || source.includes('instagram')
      || ref.includes('facebook') || ref.includes('instagram')) return REFERRER_LABELS.facebook;
  if (ref.includes('book.thegpservice') || ref.includes('booking.thegpservice')) return REFERRER_LABELS.website;
  if (source && REFERRER_LABELS[source]) return REFERRER_LABELS[source];
  if (source) {
    return source.charAt(0).toUpperCase() + source.slice(1);
  }
  if (!ref || ref === 'direct') return REFERRER_LABELS.direct;
  try {
    const host = new URL(ref.startsWith('http') ? ref : `https://${ref}`).hostname;
    return host.replace(/^www\./, '');
  } catch (e) {
    return REFERRER_LABELS.direct;
  }
}

export function sourceHint(sourceKey, row = {}) {
  const key = sourceKey || row.sourceKey;
  if (key === 'direct') {
    return 'Direct: typed the URL, bookmark, or the browser sent no referring site. Different from Booking website, which means they clicked through from book.thegpservice.co.uk.';
  }
  if (key === 'website') {
    return 'Booking website: they clicked a link on book.thegpservice.co.uk (marketing site), not a typed URL.';
  }
  const opt = SOURCE_FILTER_OPTIONS.find((o) => o.value === key);
  return (opt && opt.hint) || formatReferrer(row);
}

export function formatDevice(deviceType) {
  if (!deviceType) return '';
  const key = String(deviceType).toLowerCase();
  return DEVICE_LABELS[key] || DEVICE_LABELS.desktop;
}

export function formatCameFrom(session = {}) {
  const from = formatReferrer(session);
  return from ? `${VOCAB.cameFrom} ${from}` : '';
}

/** @deprecated Use prettyStep — kept for trackingShared re-exports */
export const STEP_LABELS = Object.keys(STEPS).reduce((acc, code) => {
  acc[code] = STEPS[code].label;
  return acc;
}, {});

/** @deprecated Use prettyStatus */
export const STATUS_LABELS = Object.keys(STATUSES).reduce((acc, code) => {
  acc[code] = STATUSES[code].label;
  return acc;
}, {});
